<template>
	<div ref="page" data-component="page" :data-slug="slug">
		<slot />
	</div>
</template>

<script setup>
import { provide, nextTick, ref, onBeforeUnmount, onUnmounted, onMounted, inject } from 'vue'

import { gsap, ScrollTrigger } from 'gsap/all'

import { createPageContext } from './context'
import { createPageLoader } from './loader'

import { useScreen } from 'vue-screen'
import { useEmitter, useSeoHead } from '~/composables/core'

import.meta.client && gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
	slug: { type: String, default: 'page' },
	name: { type: String, default: 'Page' },
	type: { type: String, default: 'page' },
	metadata: { type: [Boolean, Object], default: false },
	transitionEnter: { type: Function, default: () => {} },
	transitionLeave: { type: Function, default: () => {} },
})

const page = ref(null)
const emitter = useEmitter()
const loader = createPageLoader(props.slug)
const screen = useScreen()
const runtimeConfig = useRuntimeConfig()

const scroller = inject('$scroller')

let query = null

provide(
	'pageContext',
	createPageContext({
		element: page,
		slug: props.slug,
		name: props.name,
		loader: loader,
		scroller: scroller,
		transitionEnter: props.transitionEnter,
		transitionLeave: props.transitionLeave,
	})
)

defineExpose({ loader, page })

onBeforeUnmount(() => {
	loader.dispatch('destroyed')
	query && query.removeEventListener('change', onChange)
})

onUnmounted(() => {
	loader.dispatch('destroyed')
})

onMounted(() => {
	query = window.matchMedia('(pointer:coarse)')
	query.addEventListener('change', onChange)

	setCSSVars(screen)

	nextTick().then(() => {
		loader.dispatch('mounted')
		document.fonts.ready.then(() => {
			emitter.emit('scroller-resize')
		})
	})
})

function setCSSVars(newVal) {
	if (newVal.touch) {
		document.querySelector(':root').style.setProperty('--computed-100vh', `${newVal.height}px`)
		document.querySelector(':root').style.setProperty('--native-scrollbar-width', `0px`)
	} else {
		const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
		document.querySelector(':root').style.setProperty('--native-scrollbar-width', `${scrollbarWidth}px`)
		document.querySelector(':root').style.setProperty('--computed-100vh', `100vh`)
	}
}

function onChange() {
	window.location.reload()
}

const meta = {
	sitename: props.metadata.sitename,
	sitedescription: props.metadata.sitedescription,
	title: props.metadata.title,
	description: props.metadata.description,
	keywords: props.metadata.keywords,
	alternates: props.metadata.alternates,
	image: props.metadata.image
		? {
				url: props.metadata.image,
				width: 1200,
				height: 600,
		  }
		: false,
	head: {
		tags: [
			{ name: 'google-site-verification', content: '' },
			{ name: 'facebook-domain-verification', content: '' },
		],
		scripts: runtimeConfig.public.isDev
			? []
			: [
					{
						async: true,
						defer: true,
						src: 'https://cloud.umami.is/script.js',
						'data-website-id': '9dad25dd-085d-4af2-87de-3ee763bfe0aa',
						'data-domains': 'silkriver.com, www.silkriver.com',
					},
			  ],
	},
}

useSeoHead(meta)
</script>

<style lang="scss" scoped>
[data-component='page'] {
	position: relative;
	z-index: 9;
}
</style>
